import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, IconButton, MenuItem, Paper, Snackbar, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  DescriptionOutlined as ResumeIcon, HowToRegOutlined as MoveIcon,
  PersonSearch as ApplicantsIcon, RefreshOutlined as RefreshIcon,
} from '@mui/icons-material';
import {
  BODY_CELL_SX, HEAD_CELL_SX, TABLE_PAPER_SX, TABLE_ROW_SX,
} from '../common/tableStyles';
import { formatDate } from '../../utils/dateFormat';
import {
  getJobApplications, moveApplicationToCandidates, updateJobApplicationStatus,
} from '../../api';

// Mirrors JobOpening.Company on the backend.
const COMPANY_OPTIONS = [
  { value: 'ats', label: 'Aditya Trading' },
  { value: 'pocketx', label: 'PocketX' },
];
const STATUS_LABELS = {
  received: 'Received', reviewing: 'Reviewing', shortlisted: 'Shortlisted',
  rejected: 'Rejected', hired: 'Hired',
};
const STATUS_COLORS = { shortlisted: 'success', hired: 'success', rejected: 'error', reviewing: 'info' };

export default function JobApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      setLoading(true); setError('');
      setApplications(await getJobApplications());
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not load applications.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return applications.filter((a) => {
      if (companyFilter !== 'all' && a.applied_via !== companyFilter) return false;
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (!needle) return true;
      return `${a.full_name} ${a.email} ${a.phone} ${a.role} ${a.requisition_id}`.toLowerCase().includes(needle);
    });
  }, [applications, query, companyFilter, statusFilter]);

  const changeStatus = async (application, status) => {
    const previous = applications;
    // Optimistic: the dropdown should not sit on the old value while saving.
    setApplications((current) => current.map((a) => (a.id === application.id ? { ...a, status } : a)));
    try {
      await updateJobApplicationStatus({ id: application.id, status });
    } catch (err) {
      setApplications(previous);
      setError('Could not update the application status.');
    }
  };

  const moveToCandidates = async (application) => {
    try {
      setBusyId(application.id); setError('');
      const result = await moveApplicationToCandidates(application.id);
      setToast(result.message || 'Added to Candidates.');
      await load();
    } catch (err) {
      setError('Could not move this applicant into Candidates.');
    } finally { setBusyId(null); }
  };

  const counts = useMemo(() => ({
    total: applications.length,
    ats: applications.filter((a) => a.applied_via === 'ats').length,
    pocketx: applications.filter((a) => a.applied_via === 'pocketx').length,
  }), [applications]);

  return <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2} mb={3}>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <ApplicantsIcon color="primary" />
          <Typography variant="h5" fontWeight={800}>Job Applications</Typography>
        </Stack>
        <Typography color="text.secondary">
          Candidates who applied through the careers websites. {counts.total} total, {counts.ats} Aditya Trading, {counts.pocketx} PocketX
        </Typography>
      </Box>
      <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
    </Stack>

    {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

    <Paper elevation={0} sx={{ ...TABLE_PAPER_SX, mb: 2, p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField size="small" placeholder="Search name, email, phone or role" value={query}
          onChange={(e) => setQuery(e.target.value)} sx={{ width: { xs: '100%', sm: 420 } }} />
        <TextField select size="small" label="Company" value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)} sx={{ minWidth: 190 }}>
          <MenuItem value="all">All companies</MenuItem>
          {COMPANY_OPTIONS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="all">All statuses</MenuItem>
          {Object.entries(STATUS_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
        </TextField>
      </Stack>
    </Paper>

    <TableContainer component={Paper} sx={TABLE_PAPER_SX}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={HEAD_CELL_SX}>Applicant</TableCell>
            <TableCell sx={HEAD_CELL_SX}>Applied for</TableCell>
            <TableCell sx={HEAD_CELL_SX}>Applied via</TableCell>
            <TableCell sx={HEAD_CELL_SX}>Experience / Location</TableCell>
            <TableCell sx={HEAD_CELL_SX}>Applied on</TableCell>
            <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
            <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'right' }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading && visible.map((a) => <TableRow key={a.id} sx={TABLE_ROW_SX}>
            <TableCell sx={BODY_CELL_SX}>
              <Typography fontWeight={700} fontSize="0.875rem">{a.full_name}</Typography>
              <Typography variant="caption" color="text.secondary">{a.email} &middot; {a.phone}</Typography>
            </TableCell>
            <TableCell sx={BODY_CELL_SX}>
              {a.role}
              <br /><Typography variant="caption" color="text.secondary">{a.requisition_id}</Typography>
            </TableCell>
            <TableCell sx={BODY_CELL_SX}>
              <Chip size="small" variant="outlined" color={a.applied_via === 'pocketx' ? 'secondary' : 'primary'}
                label={a.applied_via_label || a.applied_via} />
            </TableCell>
            <TableCell sx={BODY_CELL_SX}>
              {a.experience_years} yrs
              <br /><Typography variant="caption" color="text.secondary">{a.current_location || '-'}</Typography>
            </TableCell>
            <TableCell sx={BODY_CELL_SX}>{formatDate(a.created_at)}</TableCell>
            <TableCell sx={BODY_CELL_SX}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TextField select size="small" value={a.status} onChange={(e) => changeStatus(a, e.target.value)} sx={{ minWidth: 145 }}>
                  {Object.entries(STATUS_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
                <Chip size="small" label={STATUS_LABELS[a.status] || a.status} color={STATUS_COLORS[a.status] || 'default'} />
              </Stack>
            </TableCell>
            <TableCell sx={{ ...BODY_CELL_SX, textAlign: 'right', whiteSpace: 'nowrap' }}>
              <Tooltip title={a.resume_url ? 'Open resume' : 'No resume uploaded'}>
                <span>
                  <IconButton href={a.resume_url || undefined} target="_blank" rel="noopener noreferrer" disabled={!a.resume_url}>
                    <ResumeIcon />
                  </IconButton>
                </span>
              </Tooltip>
              {a.converted_candidate
                ? <Chip size="small" color="success" variant="outlined" label="In Candidates" sx={{ ml: 1 }} />
                : <Button size="small" variant="outlined" startIcon={<MoveIcon />} sx={{ ml: 1 }}
                    disabled={busyId === a.id} onClick={() => moveToCandidates(a)}>
                    {busyId === a.id ? 'Moving...' : 'Move to Candidates'}
                  </Button>}
            </TableCell>
          </TableRow>)}
          {!loading && !visible.length && <TableRow>
            <TableCell colSpan={7} align="center" sx={{ py: 7 }}>
              {applications.length ? 'No applications match these filters.' : 'No one has applied through the careers websites yet.'}
            </TableCell>
          </TableRow>}
          {loading && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 7 }}>Loading applications...</TableCell></TableRow>}
        </TableBody>
      </Table>
    </TableContainer>

    <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast('')}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert severity="success" onClose={() => setToast('')}>{toast}</Alert>
    </Snackbar>
  </Box>;
}
